/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  MagnifyingGlass, FileText, CheckCircle, XCircle, WarningOctagon,
  ArrowClockwise, Clock, X, DownloadSimple, CurrencyDollarSimple,
  Timer, Receipt,
} from "@phosphor-icons/react";

// Cada status carrega um DOT + label + ordem lógica. Cores foram
// escolhidas para bater com o restante do painel admin (dark ambient).
const STATUS_META = {
  pending:    { label: "Pendente",   dot: "bg-amber-400",  icon: Clock },
  processing: { label: "Emitindo",   dot: "bg-blue-400",   icon: ArrowClockwise },
  retrying:   { label: "Retentando", dot: "bg-orange-400", icon: ArrowClockwise },
  issued:     { label: "Emitida",    dot: "bg-emerald-400", icon: CheckCircle },
  failed:     { label: "Falha",      dot: "bg-rose-400",   icon: WarningOctagon },
  cancelled:  { label: "Cancelada",  dot: "bg-slate-500",  icon: XCircle },
};
const STATUS_ORDER = ["pending", "processing", "retrying", "issued", "failed", "cancelled"];

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtCnpj(digits) {
  const c = String(digits || "").replace(/\D/g, "");
  if (c.length !== 14) return digits || "—";
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12, 14)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function fmtPeriod(startIso, endIso) {
  if (!startIso && !endIso) return "—";
  const f = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  };
  const a = f(startIso), b = f(endIso);
  return a && b ? `${a} → ${b}` : (a || b || "—");
}

function fmtDuration(secs) {
  if (secs == null) return "—";
  const s = Number(secs);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${Math.round(s / 86400)}d`;
}

function KpiCard({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="rounded-lg border border-white/5 bg-white/[0.04] px-4 py-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-white">{value}</div>
    </div>
  );
}

export default function AdminFiscalInvoices() {
  const [items, setItems] = useState([]);
  const [tenantsMap, setTenantsMap] = useState({});
  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [cnpjFilter, setCnpjFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [openInvoice, setOpenInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        status: status || undefined,
        q: q || undefined,
        cnpj: cnpjFilter || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        environment,
        limit: 100,
      };
      const [list, sum, k] = await Promise.all([
        api.get("/admin/fiscal-invoices", { params }),
        api.get("/admin/fiscal-invoices/summary", { params: { environment } }),
        api.get("/admin/fiscal-invoices/kpis", { params }),
      ]);
      setItems(list.data.items || []);
      setTenantsMap(list.data.tenants || {});
      setSummary(sum.data);
      setKpis(k.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível carregar as NF-e.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, environment]);

  const totals = useMemo(() => summary?.by_status || {}, [summary]);

  const exportFile = async (fmt) => {
    try {
      const params = new URLSearchParams({
        fmt,
        environment,
        ...(status && { status }),
        ...(q && { q }),
        ...(cnpjFilter && { cnpj: cnpjFilter }),
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
      });
      const token = localStorage.getItem("agentos_token") || "";
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/fiscal-invoices/export?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`export ${fmt} failed`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiscal_invoices.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} gerado.`);
    } catch (e) {
      toast.error("Não foi possível gerar o export.");
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto text-slate-100" data-testid="admin-fiscal-invoices-page">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Fiscal · NFS-e</div>
          <h1 className="font-heading text-2xl font-semibold mt-1">Notas Fiscais</h1>
          <p className="text-[13px] text-slate-400 mt-1 max-w-2xl">
            Cobranças pagas via Stripe preparadas para emissão de NF-e.
            Retry automático (5min → 30min → 2h) para falhas transitórias do emissor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            data-testid="fiscal-env-select"
            className="h-9 px-3 bg-white/5 border border-white/10 rounded-md text-[12.5px] text-white"
          >
            <option value="production">Produção</option>
            <option value="homologation">Homologação</option>
            <option value="all">Ambos</option>
          </select>
          <button
            type="button"
            onClick={() => exportFile("csv")}
            data-testid="fiscal-export-csv-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-[12.5px]"
          >
            <DownloadSimple size={13} /> CSV
          </button>
          <button
            type="button"
            onClick={() => exportFile("xlsx")}
            data-testid="fiscal-export-xlsx-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-[12.5px]"
          >
            <DownloadSimple size={13} /> Excel
          </button>
        </div>
      </header>

      {/* KPIs financeiros — v0.95.17 */}
      {kpis ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <KpiCard testid="kpi-total-billed" icon={CurrencyDollarSimple} label="Faturado total" value={fmtBRL(kpis.total_billed_cents)} />
          <KpiCard testid="kpi-month-billed" icon={CurrencyDollarSimple} label="Faturado no mês" value={fmtBRL(kpis.month_billed_cents)} />
          <KpiCard testid="kpi-today-billed" icon={CurrencyDollarSimple} label="Faturado hoje" value={fmtBRL(kpis.today_billed_cents)} />
          <KpiCard testid="kpi-avg-ticket" icon={Receipt} label="Ticket médio" value={fmtBRL(kpis.avg_ticket_cents)} />
          <KpiCard
            testid="kpi-avg-time"
            icon={Timer}
            label="Tempo médio emissão"
            value={
              kpis.avg_time_to_issue_seconds != null
                ? fmtDuration(kpis.avg_time_to_issue_seconds)
                : "—"
            }
          />
        </div>
      ) : null}

      {/* KPIs por status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {STATUS_ORDER.map((s) => {
          const meta = STATUS_META[s];
          const t = totals[s] || { count: 0, sum_cents: 0 };
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(status === s ? "" : s)}
              data-testid={`fiscal-kpi-${s}`}
              className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                status === s
                  ? "bg-white/10 border-white/20"
                  : "bg-white/[0.03] border-white/5 hover:bg-white/[0.06] hover:border-white/10"
              }`}
            >
              <div className="flex items-center gap-2 text-[11px] text-slate-400">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {meta.label}
              </div>
              <div className="mt-1 text-xl font-semibold tabular-nums text-white">{t.count}</div>
              <div className="text-[11px] text-slate-500 tabular-nums">{fmtBRL(t.sum_cents)}</div>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Busca por CNPJ, razão social, Stripe Invoice ID ou nº NF"
            data-testid="fiscal-search-input"
            className="w-full h-9 pl-9 pr-3 bg-white/[0.04] border border-white/10 rounded-md text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <input
          type="text"
          value={cnpjFilter}
          onChange={(e) => setCnpjFilter(e.target.value.replace(/\D/g, "").slice(0, 14))}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="CNPJ (14 dígitos)"
          data-testid="fiscal-cnpj-filter"
          className="h-9 px-3 bg-white/[0.04] border border-white/10 rounded-md text-[13px] text-white tabular-nums w-[200px]"
        />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="fiscal-from-date"
          className="h-9 px-2 bg-white/[0.04] border border-white/10 rounded-md text-[12px] text-white"
        />
        <span className="text-slate-500 text-xs">até</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="fiscal-to-date"
          className="h-9 px-2 bg-white/[0.04] border border-white/10 rounded-md text-[12px] text-white"
        />
        <button
          type="button"
          onClick={load}
          data-testid="fiscal-refresh-btn"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-[12.5px]"
        >
          <ArrowClockwise size={13} /> Aplicar
        </button>
        {(status || cnpjFilter || fromDate || toDate || q) ? (
          <button
            type="button"
            onClick={() => {
              setStatus(""); setCnpjFilter(""); setFromDate(""); setToDate(""); setQ("");
              setTimeout(load, 50);
            }}
            data-testid="fiscal-clear-filters"
            className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-[12px] text-slate-400 hover:text-white"
          >
            <X size={12} /> limpar
          </button>
        ) : null}
      </div>

      {/* Tabela */}
      <div className="border border-white/5 rounded-lg overflow-hidden" data-testid="fiscal-table">
        <table className="w-full min-w-[720px] text-[12.5px]">
          <thead className="bg-white/[0.04] text-slate-400 text-[10.5px] uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Cliente</th>
              <th className="px-4 py-2.5 text-left font-medium">CNPJ</th>
              <th className="px-4 py-2.5 text-right font-medium">Valor</th>
              <th className="px-4 py-2.5 text-left font-medium">Período</th>
              <th className="px-4 py-2.5 text-left font-medium">Status</th>
              <th className="px-4 py-2.5 text-left font-medium">Stripe Invoice</th>
              <th className="px-4 py-2.5 text-left font-medium">Nº NF</th>
              <th className="px-4 py-2.5 text-left font-medium">Emitida em</th>
              <th className="px-4 py-2.5 text-left font-medium">Erro</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">Carregando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                <FileText size={24} className="mx-auto mb-2 opacity-40" />
                Nenhuma NF-e encontrada.
              </td></tr>
            ) : items.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const customer = it.customer || {};
              const tname = customer.company_legal_name
                || (tenantsMap[it.tenant_id]?.company_legal_name)
                || (tenantsMap[it.tenant_id]?.name)
                || "—";
              return (
                <tr
                  key={it.id}
                  className="hover:bg-white/[0.03] transition-colors"
                  data-testid={`fiscal-row-${it.id}`}
                >
                  <td className="px-4 py-2.5">
                    <div className="text-white truncate max-w-[220px]">{tname}</div>
                    {customer.company_trade_name ? (
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{customer.company_trade_name}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-slate-300 tabular-nums">{fmtCnpj(customer.cnpj)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-white">{fmtBRL(it.amount_cents)}</td>
                  <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{fmtPeriod(it.billing_period_start, it.billing_period_end)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon size={12} className={
                        it.status === "issued" ? "text-emerald-400"
                        : it.status === "failed" ? "text-rose-400"
                        : it.status === "processing" ? "text-blue-400"
                        : it.status === "retrying" ? "text-orange-400"
                        : it.status === "cancelled" ? "text-slate-500"
                        : "text-amber-400"
                      } />
                      <span className="text-slate-200">{meta.label}</span>
                      {it.status === "retrying" && it.retry_state?.attempt_count ? (
                        <span className="text-[10px] text-orange-300 tabular-nums">
                          ({it.retry_state.attempt_count}/{it.retry_state.max_attempts || 3})
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 font-mono text-[11px] truncate max-w-[160px]">{it.stripe_invoice_id}</td>
                  <td className="px-4 py-2.5 text-slate-300">{it.invoice_number || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{fmtDate(it.issued_at)}</td>
                  <td className="px-4 py-2.5 text-rose-300 truncate max-w-[180px]" title={it.error_message || ""}>
                    {it.error_message || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => setOpenInvoice(it)}
                      data-testid={`fiscal-open-${it.id}`}
                      className="text-[11.5px] text-slate-300 hover:text-white underline underline-offset-2"
                    >
                      abrir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openInvoice ? (
        <InvoiceDetailDrawer
          invoice={openInvoice}
          tenant={tenantsMap[openInvoice.tenant_id]}
          onClose={() => setOpenInvoice(null)}
          onSaved={() => { setOpenInvoice(null); load(); }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────── Drawer de detalhe / edição ───────────────────

function InvoiceDetailDrawer({ invoice, tenant, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    status: invoice.status || "pending",
    invoice_number: invoice.invoice_number || "",
    verification_code: invoice.verification_code || "",
    pdf_url: invoice.pdf_url || "",
    xml_url: invoice.xml_url || "",
    provider: invoice.provider || "manual",
    provider_invoice_id: invoice.provider_invoice_id || "",
    error_message: invoice.error_message || "",
    cancel_reason: invoice.cancel_reason || "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/admin/fiscal-invoices/${invoice.id}`);
        if (mounted) setDetail(r.data);
      } catch (e) {
        toast.error("Não foi possível carregar detalhes.");
      }
    })();
    return () => { mounted = false; };
  }, [invoice.id]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const patch = async (overrides = {}) => {
    const body = { ...form, ...overrides };
    // Remove strings vazias — o backend ignora com exclude_none, mas
    // strings vazias sobrescreveriam valores prévios (issued_at, etc).
    Object.keys(body).forEach((k) => {
      if (body[k] === "" || body[k] === null || body[k] === undefined) delete body[k];
    });
    setSaving(true);
    try {
      await api.patch(`/admin/fiscal-invoices/${invoice.id}`, body);
      toast.success("NF-e atualizada.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const markIssued = () => {
    if (!form.invoice_number.trim()) {
      toast.error("Informe o número da NF antes de marcar como emitida.");
      return;
    }
    patch({ status: "issued" });
  };
  const markFailed = () => {
    if (!form.error_message.trim()) {
      toast.error("Descreva o motivo da falha em 'Mensagem de erro'.");
      return;
    }
    patch({ status: "failed" });
  };
  const markCancelled = () => {
    if (!form.cancel_reason.trim()) {
      toast.error("Informe o motivo do cancelamento.");
      return;
    }
    patch({ status: "cancelled" });
  };

  const customer = invoice.customer || {};
  const t = detail?.tenant || tenant || {};

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <aside
        className="w-full max-w-[720px] h-screen bg-[#0F0F12] border-l border-white/10 overflow-y-auto text-slate-100"
        onClick={(e) => e.stopPropagation()}
        data-testid="fiscal-drawer"
      >
        <div className="sticky top-0 z-10 bg-[#0F0F12]/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">NF-e</div>
            <div className="font-heading text-lg mt-0.5">{customer.company_legal_name || t.name || "—"}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="fiscal-drawer-close"
            className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Dados fiscais do cliente (imutável — snapshot) */}
          <section className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">Dados fiscais (snapshot no pagamento)</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
              <FiscalDL label="CNPJ" value={fmtCnpj(customer.cnpj)} />
              <FiscalDL label="Email fiscal" value={customer.fiscal_email || "—"} />
              <FiscalDL label="Razão social" value={customer.company_legal_name || "—"} />
              <FiscalDL label="Nome fantasia" value={customer.company_trade_name || "—"} />
              <FiscalDL label="Insc. Municipal" value={customer.municipal_registration || "—"} />
              <FiscalDL label="Insc. Estadual" value={customer.state_registration || "—"} />
              <FiscalDL span
                label="Endereço"
                value={
                  [
                    customer.billing_address?.street,
                    customer.billing_address?.number ? `, ${customer.billing_address.number}` : "",
                    customer.billing_address?.complement ? ` — ${customer.billing_address.complement}` : "",
                    customer.billing_address?.district ? ` · ${customer.billing_address.district}` : "",
                    customer.billing_address?.city ? ` · ${customer.billing_address.city}` : "",
                    customer.billing_address?.state ? `/${customer.billing_address.state}` : "",
                    customer.billing_address?.zip_code ? ` · CEP ${customer.billing_address.zip_code}` : "",
                  ].filter(Boolean).join("") || "—"
                }
              />
            </dl>
          </section>

          {/* Detalhes da cobrança */}
          <section className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">Cobrança</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
              <FiscalDL label="Valor" value={fmtBRL(invoice.amount_cents)} />
              <FiscalDL label="Moeda" value={String(invoice.currency || "brl").toUpperCase()} />
              <FiscalDL label="Stripe Invoice" value={invoice.stripe_invoice_id} monospace />
              <FiscalDL label="Stripe Subscription" value={invoice.stripe_subscription_id || "—"} monospace />
              <FiscalDL label="Stripe Customer" value={invoice.stripe_customer_id || "—"} monospace />
              <FiscalDL label="Payment Intent" value={invoice.stripe_payment_intent_id || "—"} monospace />
              <FiscalDL span label="Período" value={fmtPeriod(invoice.billing_period_start, invoice.billing_period_end)} />
              <FiscalDL span label="Descrição do serviço" value={invoice.service_description} />
            </dl>
          </section>

          {/* Formulário de emissão manual */}
          <section className="bg-white/[0.03] border border-white/5 rounded-lg p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Emissão manual</div>
            <div className="grid grid-cols-2 gap-3">
              <DrawerInput label="Número da NF" testid="drawer-invoice-number"
                value={form.invoice_number} onChange={(v) => set("invoice_number", v)} placeholder="123456" />
              <DrawerInput label="Código de verificação" testid="drawer-verification-code"
                value={form.verification_code} onChange={(v) => set("verification_code", v)} placeholder="ABC123" />
              <DrawerInput label="URL do PDF" testid="drawer-pdf-url"
                value={form.pdf_url} onChange={(v) => set("pdf_url", v)} placeholder="https://…" span />
              <DrawerInput label="URL do XML" testid="drawer-xml-url"
                value={form.xml_url} onChange={(v) => set("xml_url", v)} placeholder="https://…" span />
              <DrawerInput label="Provider" testid="drawer-provider"
                value={form.provider} onChange={(v) => set("provider", v)} placeholder="manual, enotas, focus_nfe…" />
              <DrawerInput label="ID no provider" testid="drawer-provider-invoice-id"
                value={form.provider_invoice_id} onChange={(v) => set("provider_invoice_id", v)} />
              <DrawerInput label="Mensagem de erro" testid="drawer-error-message"
                value={form.error_message} onChange={(v) => set("error_message", v)} span />
              <DrawerInput label="Motivo do cancelamento" testid="drawer-cancel-reason"
                value={form.cancel_reason} onChange={(v) => set("cancel_reason", v)} span />
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
              <ActionBtn
                testid="drawer-mark-issued"
                onClick={markIssued}
                disabled={saving || invoice.status === "issued"}
                variant="success"
              >
                <CheckCircle size={13} weight="bold" /> Marcar como emitida
              </ActionBtn>
              <ActionBtn
                testid="drawer-mark-failed"
                onClick={markFailed}
                disabled={saving || invoice.status === "cancelled"}
                variant="warn"
              >
                <WarningOctagon size={13} weight="bold" /> Marcar falha
              </ActionBtn>
              <ActionBtn
                testid="drawer-mark-cancelled"
                onClick={markCancelled}
                disabled={saving || invoice.status !== "issued"}
                variant="danger"
              >
                <XCircle size={13} weight="bold" /> Cancelar
              </ActionBtn>
              <ActionBtn
                testid="drawer-save"
                onClick={() => patch({})}
                disabled={saving}
              >
                Salvar campos
              </ActionBtn>
            </div>
          </section>

          <section className="text-[11px] text-slate-500 leading-relaxed">
            Status atual:{" "}
            <span className="text-slate-300 font-medium" data-testid="drawer-current-status">
              {STATUS_META[invoice.status]?.label || invoice.status}
            </span>{" · "}
            Criada em {fmtDate(invoice.created_at)}{" · "}
            Última atualização {fmtDate(invoice.updated_at)}
            {invoice.environment ? (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 text-[10px] uppercase tracking-wider">
                {invoice.environment === "homologation" ? "Homologação" : "Produção"}
              </span>
            ) : null}
          </section>

          {/* v0.95.17 — Timeline de eventos (append-only) */}
          {(invoice.events || []).length > 0 ? (
            <section
              className="bg-white/[0.03] border border-white/5 rounded-lg p-4"
              data-testid="drawer-events-timeline"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">
                Trilha de auditoria ({(invoice.events || []).length})
              </div>
              <ol className="space-y-2">
                {[...(invoice.events || [])].reverse().slice(0, 20).map((ev, i) => (
                  <li key={i} className="text-[11.5px] flex gap-3">
                    <span className="text-slate-500 tabular-nums w-[95px] shrink-0">
                      {fmtDate(ev.created_at)}
                    </span>
                    <span className="text-slate-200 font-medium w-[130px] shrink-0">
                      {ev.event}
                    </span>
                    <span className="text-slate-400 truncate flex-1" title={JSON.stringify(ev.details || {})}>
                      {ev.user || "system"} · {Object.keys(ev.details || {}).length > 0
                        ? JSON.stringify(ev.details).slice(0, 80)
                        : "—"}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {/* v0.95.17 — Provider technical logs */}
          {(invoice.provider_logs || []).length > 0 ? (
            <section
              className="bg-white/[0.03] border border-white/5 rounded-lg p-4"
              data-testid="drawer-provider-logs"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">
                Logs técnicos ({invoice.provider_logs.length})
              </div>
              <ol className="space-y-2">
                {[...invoice.provider_logs].reverse().slice(0, 10).map((pl, i) => (
                  <li key={i} className="text-[11.5px] font-mono flex gap-3">
                    <span className="text-slate-500 tabular-nums w-[95px] shrink-0">
                      {fmtDate(pl.called_at)}
                    </span>
                    <span className="text-slate-200 w-[110px] shrink-0">{pl.action}</span>
                    <span className={`w-[60px] shrink-0 tabular-nums ${
                      pl.http_status && pl.http_status >= 400 ? "text-rose-300" : "text-emerald-300"
                    }`}>
                      {pl.http_status ?? "—"}
                    </span>
                    <span className="text-slate-400 tabular-nums w-[70px] shrink-0">
                      {pl.latency_ms}ms
                    </span>
                    <span className="text-slate-400 truncate flex-1" title={pl.error_message || ""}>
                      {pl.error_message || "OK"}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {/* v0.95.17 — Retry state (só mostra se está em curso) */}
          {invoice.status === "retrying" && invoice.retry_state ? (
            <section
              className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4"
              data-testid="drawer-retry-state"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-orange-300 mb-2">
                Retentativa em curso
              </div>
              <div className="text-[12px] text-orange-100">
                Tentativa {invoice.retry_state.attempt_count} de {invoice.retry_state.max_attempts || 3}
                {invoice.retry_state.next_retry_at ? (
                  <> · Próxima {fmtDate(invoice.retry_state.next_retry_at)}</>
                ) : null}
              </div>
              {invoice.retry_state.last_error ? (
                <div className="text-[11px] text-orange-200/80 mt-1 italic">
                  Último erro: {invoice.retry_state.last_error}
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function FiscalDL({ label, value, span, monospace }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</dt>
      <dd className={`mt-0.5 text-slate-200 ${monospace ? "font-mono text-[11.5px]" : ""}`}>{value}</dd>
    </div>
  );
}

function DrawerInput({ label, value, onChange, placeholder, span, testid }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full h-9 px-3 bg-black/40 border border-white/10 rounded-md text-[12.5px] text-white placeholder:text-slate-600 focus:outline-none focus:border-white/25"
      />
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, variant, testid }) {
  const cls =
    variant === "success" ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border-emerald-500/30"
      : variant === "warn" ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border-amber-500/30"
      : variant === "danger" ? "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 border-rose-500/30"
      : "bg-white/5 text-slate-100 hover:bg-white/10 border-white/10";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-[12.5px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
    >
      {children}
    </button>
  );
}
